import styles from './ProductPopupContent.scss';
import { Button } from '@flixbus/honeycomb-react';
import classnames from 'classnames';
import ProductList from 'app/components/product/ProductList';
import React, { useEffect, useState } from 'react';
import { tabTrap } from 'app/providers/tabTrap';
import { breakpointMatches, getPassengerCount } from 'app/functions';
import { useTranslations } from 'app/hooks/useTranslations';
import { a11yLabelsKeys } from 'app/providers/a11yLabels';
import {
  allProductsSelector,
  currentProductTypesExtendedSelector,
  currentProductTypesSelector,
  featureLimitedProductListSelector,
  maxTotalPassengersCountSelector,
  productListsSelector,
} from 'app/store/selectors';
import { useSelector } from 'react-redux';
import { ProductPopupContentType } from 'app/models/types/store/ui';
import { DiscountInfo } from './DiscountInfo';
import { GroupTripMessage } from './GroupTripMessage';

export const ProductPopupContent = ({
  discountsButtonWrapper,
  extendedProductsInformationLink,
  wrapperRef,
  toggleInfoMessageSeen,
  areGroupTripsAvailable,
  showExtendedProductsInfo,
  handleSetProductQuantity,
  infoMessageRef,
  backButtonWrapper,
  handleClose,
  infoMessagesSeen,
}: ProductPopupContentType) => {
  const { mainProducts: limitedMainProducts, extendedProducts: limitedExtendedProducts } =
    useSelector(productListsSelector);
  const products = useSelector(currentProductTypesSelector);
  const extendedProducts = useSelector(currentProductTypesExtendedSelector);
  const showLimitedProductList = useSelector(featureLimitedProductListSelector);
  const mainList = showLimitedProductList ? limitedMainProducts : products;
  const extendedList = showLimitedProductList ? limitedExtendedProducts : extendedProducts;
  const [isExtendedVisible, setExtendedVisible] = useState(false);
  const allProducts = useSelector(allProductsSelector);
  const passengerCount = getPassengerCount(allProducts);
  const maxTotalPassengerCount = useSelector(maxTotalPassengersCountSelector);
  const hasExtendedProducts =
    Object.keys(showLimitedProductList ? limitedExtendedProducts : extendedProducts).length > 0;
  const extendedOpen = isExtendedVisible && hasExtendedProducts;
  const extendedClosed = !isExtendedVisible && hasExtendedProducts;
  const isSmallBreakpoint = breakpointMatches('small').matches;
  const {
    getTranslationDiscountsBack,
    getTranslationBackToProductsAriaLabel,
    getTranslationDiscountsShowAriaLabel,
  } = useTranslations({
    getTranslationDiscountsBack: 'search_bundle.search_widget.passengers.popup.btn.social_hide',
    getTranslationDiscountsShowAriaLabel: a11yLabelsKeys.products.showMoreDiscounts,
    getTranslationBackToProductsAriaLabel: a11yLabelsKeys.products.backToProductSelection,
  });

  useEffect(() => {
    const buttonRef = isExtendedVisible ? backButtonWrapper : discountsButtonWrapper;
    buttonRef.current?.querySelector('button')?.focus();
  }, [isExtendedVisible]);

  return (
    <div className={styles.productWrapperList}>
      {extendedOpen && (
        <div className={styles.productWrapperExtendedHeader}>
          <span ref={backButtonWrapper}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setExtendedVisible(false);
              }}
              extraClasses={classnames(styles.productButton, styles.backButton)}
              aria-label={getTranslationBackToProductsAriaLabel()}
            >
              {getTranslationDiscountsBack()}
            </Button>
          </span>

          <DiscountInfo
            showExtendedProductsInfo={showExtendedProductsInfo}
            handleClose={handleClose}
            extendedProductsInformationLink={extendedProductsInformationLink}
            wrapperRef={wrapperRef}
          />
        </div>
      )}

      <GroupTripMessage
        infoMessageRef={infoMessageRef}
        areGroupTripsAvailable={areGroupTripsAvailable}
        passengerCount={passengerCount}
      />

      <ProductList
        products={isExtendedVisible ? extendedList : mainList}
        onSetProductQuantity={handleSetProductQuantity}
        onProductMessagesSeen={toggleInfoMessageSeen}
        productMessagesSeen={infoMessagesSeen}
        maxTotalPassengerCount={maxTotalPassengerCount}
        passengerCount={passengerCount}
        tabTrapAction={(e: React.KeyboardEvent) => {
          if (isSmallBreakpoint) {
            return;
          }

          return tabTrap({
            event: e,
            firstItemSelector: '#productSummary',
            lastItemSelector: '#lastPlus',
            isActive: !hasExtendedProducts || (hasExtendedProducts && extendedOpen),
            wrapper: wrapperRef.current,
            callback: handleClose,
            skipCheck: true,
          });
        }}
      />

      {extendedClosed && (
        <div className={styles.productWrapperExtendedFooter}>
          <span ref={discountsButtonWrapper}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setExtendedVisible(true);
              }}
              extraClasses={styles.productButton}
              aria-label={getTranslationDiscountsShowAriaLabel()}
            >
              {getTranslationDiscountsShowAriaLabel()}
            </Button>
          </span>

          <DiscountInfo
            showExtendedProductsInfo={showExtendedProductsInfo}
            handleClose={handleClose}
            extendedProductsInformationLink={extendedProductsInformationLink}
            wrapperRef={wrapperRef}
            isLast
          />
        </div>
      )}
    </div>
  );
};
